import { useState, type FormEvent } from "react";
import { useData } from "@/contexts/DataContext";
import { Plus, Pencil, Trash2, X, Upload, Search } from "lucide-react";
import { AffiliateLink } from "@/data/mockData";
import { fileUploadService } from "@/lib/fileUploadService";

const AdminAffiliates = () => {
  const { affiliates, addAffiliate, updateAffiliate, deleteAffiliate } = useData();
  const [editing, setEditing] = useState<AffiliateLink | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Define available pages
  const availablePages = [
    { value: "home", label: "Home" },
    { value: "ipos", label: "IPOs" },
    { value: "ipo-gmp", label: "IPO GMP" },
    { value: "news", label: "News" },
    { value: "jobs", label: "Jobs" },
    { value: "learning", label: "Learning" },
    { value: "refer-earn", label: "Refer & Earn" }
  ];

  const normalizePages = (pages: string[] = []) => Array.from(new Set(pages.filter(Boolean)));

  const openNew = () => { 
    setEditing({ 
      id: Date.now().toString(), 
      text: "", 
      url: "", 
      type: "text",
      imageUrl: "",
      showOnPages: [] 
    }); 
    setIsNew(true); 
  };
  
  const openEdit = (item: AffiliateLink) => { 
    setEditing({ ...item }); 
    setIsNew(false); 
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploaded = await fileUploadService.uploadFile(file);
      if (!uploaded.url) {
        throw new Error("Image upload did not return a URL");
      }

      setEditing(prev => prev ? { ...prev, imageUrl: uploaded.url } : null);
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => { 
    if (!editing) return; 
    
    const normalizedShowOnPages = normalizePages(editing.showOnPages || []);
    const payload = { ...editing, showOnPages: normalizedShowOnPages };

    console.log("AdminAffiliates: Saving affiliate:", payload);

    try {
      // Replace behavior: selected pages move from old affiliate(s) to this one.
      const conflictingAffiliates = affiliates.filter(
        (affiliate) =>
          affiliate.id !== editing.id &&
          (affiliate.showOnPages || []).some((page) => normalizedShowOnPages.includes(page))
      );

      for (const affiliate of conflictingAffiliates) {
        const updatedPages = (affiliate.showOnPages || []).filter(
          (page) => !normalizedShowOnPages.includes(page)
        );
        await updateAffiliate(affiliate.id, { showOnPages: updatedPages });
      }

      if (isNew) {
        await addAffiliate(payload);
      } else {
        await updateAffiliate(editing.id, payload);
      }

      setEditing(null);
    } catch (error) {
      console.error("AdminAffiliates: Error while saving affiliate:", error);
      alert("Failed to save affiliate. Please try again.");
    }
  };
  
  const handleDelete = (id: string) => { 
    if (confirm("Delete?")) deleteAffiliate(id); 
  };
  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredAffiliates = !normalizedSearch
    ? affiliates
    : affiliates.filter((item) =>
        [
          item.text,
          item.url,
          item.type,
          ...(item.showOnPages || []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );
  const totalPages = Math.max(1, Math.ceil(filteredAffiliates.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedAffiliates = filteredAffiliates.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-foreground">Manage Affiliates</h1>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
          <Plus size={16} /> Add Link
        </button>
      </div>
      <form onSubmit={onSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by text, url, type, page..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          <Search size={15} />
          Search
        </button>
      </form>

      <div className="finance-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-semibold">Text</th>
                <th className="text-left px-4 py-3 font-semibold">URL</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Show On Pages</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAffiliates.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">{item.text}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{item.url}</td>
                  <td className="px-4 py-3"><span className="badge-info">{item.type}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {item.showOnPages.map(page => {
                        const pageLabel = availablePages.find(p => p.value === page)?.label || page;
                        return (
                          <span key={page} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {pageLabel}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-muted"><Pencil size={15} /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredAffiliates.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">No affiliate links found.</div>
      )}
      {filteredAffiliates.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredAffiliates.length)} of {filteredAffiliates.length}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-display font-bold">{isNew ? "Add Link" : "Edit Link"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Text</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">URL</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Type</label>
                <select className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value as "text" | "banner" })}>
                  <option value="text">Text</option>
                  <option value="banner">Banner</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Image (Optional)</label>
                <div className="mt-1">
                  {editing.imageUrl ? (
                    <div className="relative">
                      <img 
                        src={editing.imageUrl} 
                        alt="Affiliate image" 
                        className="w-full h-32 object-cover rounded-lg border border-border"
                      />
                      <button
                        onClick={() => setEditing({ ...editing, imageUrl: "" })}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="affiliate-image-upload"
                      />
                      <label
                        htmlFor="affiliate-image-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                            <span className="text-sm text-muted-foreground">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload size={24} className="text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Click to upload image</span>
                            <span className="text-xs text-muted-foreground">PNG, JPG up to 10MB</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Show On Pages</label>
                <p className="text-xs text-muted-foreground mt-1">
                  Selecting a page will replace the previous affiliate on that page.
                </p>
                <div className="mt-2 space-y-2">
                  {availablePages.map(page => {
                    const isSelected = editing.showOnPages?.includes(page.value) || false;
                    
                    return (
                      <label key={page.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            console.log("Page checkbox changed:", page.value, e.target.checked);
                            if (e.target.checked) {
                              const newShowOnPages = [...(editing.showOnPages || []), page.value];
                              console.log("Adding page, new showOnPages:", newShowOnPages);
                              setEditing({
                                ...editing,
                                showOnPages: newShowOnPages
                              });
                            } else {
                              const newShowOnPages = (editing.showOnPages || []).filter(p => p !== page.value);
                              console.log("Removing page, new showOnPages:", newShowOnPages);
                              setEditing({
                                ...editing,
                                showOnPages: newShowOnPages
                              });
                            }
                          }}
                          className="rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="text-sm">{page.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-6 pt-0 border-t border-border">
              <button onClick={handleSave} className="flex-1 py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-base hover:bg-primary/90 transition-colors">Save</button>
              <button onClick={() => setEditing(null)} className="flex-1 py-3 rounded-lg bg-muted text-foreground font-semibold text-base hover:bg-muted/80 transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliates;
